import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { cajaService, Caja } from '../../services/cajaService';
import { sucursalService } from '../../services/sucursalService';
import CajaForm from '../../components/Cajas/CajaForm';

const CajasList = () => {
  const [page, setPage] = useState(1);
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);
  const [editingCaja, setEditingCaja] = useState<Caja | null>(null);
  const queryClient = useQueryClient();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['cajas', page, sucursalFilter],
    () => cajaService.getAll(page, 10, sucursalFilter)
  );

  const deleteMutation = useMutation(cajaService.delete, {
    onSuccess: () => {
      toast.success('Caja eliminada correctamente');
      queryClient.invalidateQueries(['cajas']);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al eliminar caja');
    }
  });

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de eliminar esta caja?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (caja: Caja) => {
    setEditingCaja(caja);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCaja(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Cajas</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nueva Caja
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="flex space-x-4">
            <select
              value={sucursalFilter || ''}
              onChange={(e) => {
                setSucursalFilter(e.target.value ? Number(e.target.value) : undefined);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.cajas.map((caja) => (
                <tr key={caja.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{caja.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {caja.sucursal?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{caja.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caja.numeroCaja}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${caja.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {caja.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(caja)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(caja.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 10) + 1} a {Math.min(page * 10, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <CajaForm
          caja={editingCaja}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default CajasList;

